import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Wrench, FileText, MapPin, CalendarDays, ArrowRight, FileDown, Mail, Check } from "lucide-react";
import { DuesAndPaymentsCard } from "../../components/customer/DuesAndPaymentsCard";
import { toast } from "sonner";
import { formatError } from "../../lib/api";

const fmt = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [plots, setPlots] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statements, setStatements] = useState([]);
  const [ccEmail, setCcEmail] = useState("");
  const [savingPref, setSavingPref] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    const [s, p, a, h] = await Promise.all([
      api.get("/summary"),
      api.get("/plots"),
      api.get("/activities"),
      api.get(`/owners/${user.id}/statement-history`).catch(() => ({ data: [] })),
    ]);
    setSummary(s.data); setPlots(p.data); setActivities(a.data.slice(0, 8));
    setStatements(h.data || []);
  }, [user?.id]);

  useEffect(() => { setCcEmail(user?.statement_cc_email || ""); }, [user?.statement_cc_email]);

  const saveCcPref = async (e) => {
    e.preventDefault();
    setSavingPref(true);
    try {
      await api.patch(`/users/${user.id}/profile`, { statement_cc_email: ccEmail.trim() || null });
      toast.success(ccEmail.trim() ? "We'll copy statements to that address" : "Copies turned off");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSavingPref(false); }
  };
  useEffect(() => { load(); }, [load]);

  const KPI = ({ label, value, icon: Icon, testid }) => (
    <div className="p-6 rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{label}</div>
        <Icon size={16} className="text-primary" />
      </div>
      <div className="heading text-3xl mt-4">{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Welcome back</div>
        <h1 className="heading text-3xl md:text-4xl mt-1">Hey, {user?.name?.split(" ")[0]}.</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <KPI label="My plots" value={summary?.plots} icon={MapPin} testid="kpi-plots" />
        <KPI label="Documents" value={summary?.documents} icon={FileText} testid="kpi-docs" />
        <KPI label="Bookings" value={summary?.bookings} icon={CalendarDays} testid="kpi-bookings" />
        <KPI label="Open tasks" value={summary?.maintenance_open} icon={Wrench} testid="kpi-maint" />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-xl">Recent activity on your plots</h2>
            <Link to="/app/plots" className="text-xs text-primary flex items-center gap-1">See plots <ArrowRight size={12} /></Link>
          </div>
          <div className="border border-border rounded-lg bg-card divide-y divide-border" data-testid="activity-timeline">
            {activities.length === 0 && <div className="p-6 text-sm text-muted-foreground">No activity yet.</div>}
            {activities.map((a) => (
              <div key={a.id} className="p-4 flex gap-4 hover:bg-secondary/40 transition-[background-color] duration-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground w-24 shrink-0">{a.activity_type}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  {a.description && <div className="text-xs text-muted-foreground mt-0.5">{a.description}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(a.performed_at).toLocaleString()} · {a.performed_by}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="heading text-xl mb-3">Your plots</h2>
          <div className="space-y-3" data-testid="my-plots-list">
            {plots.map((p) => (
              <Link key={p.id} to={`/app/plots/${p.id}`} className="block p-4 rounded-lg border border-border bg-card hover:-translate-y-0.5 transition-transform duration-200">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Plot {p.plot_number}</div>
                  {p.has_cottage && <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary rounded px-2 py-0.5">Cottage</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{p.size_sqft ? `${Math.round(p.size_sqft).toLocaleString("en-IN")} sq ft` : "—"}</div>
              </Link>
            ))}
            {plots.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-4">No plots assigned yet.</div>}
          </div>
          <div className="mt-6">
            <DuesAndPaymentsCard />
          </div>
          {statements.length > 0 && (
            <div className="mt-6" data-testid="my-statements">
              <h2 className="heading text-xl mb-3">Statements sent to me</h2>
              <div className="border border-border rounded-lg bg-card divide-y divide-border">
                {statements.map((h, i) => (
                  <div key={h.at || `stmt-${i}`} className="p-4" data-testid={`my-stmt-${i}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-sm font-medium">
                        {new Date(h.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-xs text-primary font-medium">{h.count} invoice(s) · {fmt(h.total)}</div>
                    </div>
                    {h.note && <div className="mt-1 text-xs italic text-muted-foreground">“{h.note}”</div>}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/owners/${user.id}/overdue-statement`}
                       target="_blank" rel="noreferrer"
                       className="mt-2 text-xs inline-flex items-center gap-1 text-primary hover:underline"
                       data-testid={`my-stmt-pdf-${i}`}>
                      <FileDown size={11} /> Latest statement PDF
                    </a>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">The PDF above always reflects your current dues.</p>
            </div>
          )}
          <div className="mt-6 p-4 rounded-lg border border-border bg-card" data-testid="notif-pref">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={14} className="text-primary" />
              <div className="text-sm font-medium">Notifications</div>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">Copy every statement email to a partner or accountant. Leave blank to turn off.</p>
            <form onSubmit={saveCcPref} className="flex gap-2 items-center">
              <input type="email" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)}
                     placeholder="accountant@family.in"
                     className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                     data-testid="cc-email-input" />
              <button type="submit" disabled={savingPref}
                      className="text-xs bg-primary text-primary-foreground rounded-md px-3 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                      data-testid="cc-email-save">
                <Check size={12} /> {savingPref ? "Saving…" : "Save"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
